import React from 'react';
import Icons from '../../../components/Icons/Icons';
import Slider from 'react-slick';

import bdeIcon from '../../../assets/EnhancedServices/BusinessDevelopmentIcon.png';
import contentCreationIcon from '../../../assets/EnhancedServices/ContentCreationIcon.png';
import customServiceIcon from '../../../assets/EnhancedServices/CustomServicesIcon.png';
import exclusiveTrainingIcon from '../../../assets/EnhancedServices/MentorshipIcon_1.png';
import exclusiveTraining1Icon from '../../../assets/EnhancedServices/MentorshipIcon.png';
import socialMediaIcon from '../../../assets/EnhancedServices/SocialMediaIcon.png';

import css from './Sections.module.css';
import '../../../styles/slick/slick.css';
import '../../../styles/slick/slick-theme.css';
import classNames from 'classnames';
import { TalentPlusService } from '../../../util/enums';
import { FormattedMessage } from 'react-intl';

// Mapping of service keys to TalentPlusService enum values
const serviceKeyToEnumValue = {
  exclusiveTraining: TalentPlusService.EXCLUSIVE_TRAINING,
  mentorship: TalentPlusService.MENTORSHIP,
  businessDevelopment: TalentPlusService.BUSINESS_DEVELOPMENT,
  contentCreation: TalentPlusService.CONTENT_CREATION,
  socialMedia: TalentPlusService.SOCIAL_MEDIA,
  customService: TalentPlusService.CUSTOM_SERVICE,
};

export const getEnhancedServices = (intl, talentPlusMaybe) => {
  // Fetch predefined services
  const predefinedServices = [
    {
      icon: <img src={exclusiveTrainingIcon} alt="Exclusive Training" />,
      key: 'exclusiveTraining',
      title: intl.formatMessage({ id: 'EnhancedServices.ExclusiveTraining.title' }),
      description: intl.formatMessage({ id: 'EnhancedServices.ExclusiveTraining.description' }),
      isImage: true,
      linkName: intl.formatMessage({ id: 'EnhancedServices.ExclusiveTraining.linkName' }),
      linkDestination: 'SearchPage',
    },
    {
      icon: <img src={exclusiveTraining1Icon} alt="Mentorship" />,
      key: 'mentorship',
      isImage: true,
      title: intl.formatMessage({ id: 'EnhancedServices.Mentorship.title' }),
      description: intl.formatMessage({ id: 'EnhancedServices.Mentorship.description' }),
      linkName: intl.formatMessage({ id: 'EnhancedServices.Mentorship.linkName' }),
      linkDestination: 'SearchPage',
    },
    {
      icon: <img src={bdeIcon} alt="Business Development" />,
      key: 'businessDevelopment',
      isImage: true,
      title: intl.formatMessage({ id: 'EnhancedServices.BusinessDevelopment.title' }),
      description: intl.formatMessage({ id: 'EnhancedServices.BusinessDevelopment.description' }),
      linkName: intl.formatMessage({ id: 'EnhancedServices.BusinessDevelopment.linkName' }),
      linkDestination: 'SearchPage',
    },
    {
      icon: <img src={contentCreationIcon} alt="Content Creation" />,
      key: 'contentCreation',
      isImage: true,
      title: intl.formatMessage({ id: 'EnhancedServices.ContentCreation.title' }),
      description: intl.formatMessage({ id: 'EnhancedServices.ContentCreation.description' }),
      linkName: intl.formatMessage({ id: 'EnhancedServices.ContentCreation.linkName' }),
      linkDestination: 'SearchPage',
    },
    {
      icon: <img src={socialMediaIcon} alt="Social Media" />,
      key: 'socialMedia',
      isImage: true,
      title: intl.formatMessage({ id: 'EnhancedServices.SocialMedia.title' }),
      description: intl.formatMessage({ id: 'EnhancedServices.SocialMedia.description' }),
      linkName: intl.formatMessage({ id: 'EnhancedServices.SocialMedia.linkName' }),
      linkDestination: 'SearchPage',
    },
  ];

  // **Include Custom Services**
  const customServices = talentPlusMaybe
    .filter(service => service.type === 'Custom service')
    .map(customService => ({
      icon: <img src={customServiceIcon} alt={customService.service} />,
      key: `customService`, // Ensure uniqueness
      isImage: true,
      title: customService.service, // **Use the real service name**
      description: intl.formatMessage({ id: 'EnhancedServices.Custom.description' }),
      linkName: intl.formatMessage({ id: 'EnhancedServices.Custom.linkName' }),
      linkDestination: 'SearchPage',
    }));

  return [...predefinedServices, ...customServices];
};

function Step({ step, onSelectService }) {
  return (
    <div className={css.block}>
      <div className={css.blockTopInfo}>
        <div
          className={classNames(css.icons, css.signatureIcons)}
          role="button"
          onClick={onSelectService}
        >
          {step?.isImage ? step?.icon : <Icons name={step?.icon} />}
        </div>
        <h3>{step?.title}</h3>
        <p>{step?.description}</p>
      </div>
      <span role="button" onClick={onSelectService} className={css.findLink}>
        {step?.linkName} <Icons name="rightArrow" />{' '}
      </span>
    </div>
  );
}

function EnhancedServices(props) {
  const { intl, onSelectService = () => {}, publicData = {}, isMobileLayout } = props;

  const talentPlusMaybe = publicData?.talentPlus || [];

  // Get all enhanced services with dynamically added custom services
  const allSteps = getEnhancedServices(intl, talentPlusMaybe);

  // Filter steps to only include services that are in TalentPlusService enum and present in talentPlusMaybe
  const steps = allSteps.filter(step => {
    // Check if the service key is in the TalentPlusService enum
    const isInEnum = serviceKeyToEnumValue[step.key];
    if (!isInEnum) return false; // Exclude services not in the enum (if any)

    // Check if the service exists in talentPlusMaybe
    return talentPlusMaybe.some(talentPlusItem => {
      // Match both normal and custom services
      return (
        talentPlusItem.service === step.title || // Match regular services
        (step.key === 'customService' && talentPlusItem.type === 'Custom service')
      );
    });
  });

  // If no steps are available after filtering, return null to avoid rendering the section
  if (!steps.length) {
    return null;
  }

  return (
    <div className={css.howItWorks}>
      <div className={css.contentWidth}>
        <div className={css.sectionHead}>
          <div className={css.leftSec}>
            <h2>
              <FormattedMessage id="ListingPageEnhancedServices.title" />
            </h2>
            {/* <p className={css.sectionDescription}>
              <FormattedMessage id="ListingPageEnhancedServices.description" />
            </p>
            <ol className={css.sectionDescriptionList}>
              <li>
                <FormattedMessage id="ListingPageEnhancedServices.step1" />
              </li>
              <li>
                <FormattedMessage id="ListingPageEnhancedServices.step2" />
              </li>
            </ol> */}
          </div>
        </div>
        <div className={css.sectionContent}>
          <div className={css.featuredListingsContent}>
            <div className={css.templatesSlider}>
              {steps?.length > 1 ? (
                <Slider {...{ ...sliderSettings, arrows: isMobileLayout ? false : true }}>
                  {steps.map((step, index) => (
                    <Step
                      key={index}
                      step={step}
                      onSelectService={() => onSelectService(step?.key)}
                    />
                  ))}
                </Slider>
              ) : (
                <div className={css.singleSlider}>
                  <Step
                    key={steps[0]}
                    step={steps[0]}
                    onSelectService={() => onSelectService(steps[0]?.key)}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Default slider settings
const sliderSettings = {
  dots: false,
  arrows: true,
  infinite: false,
  autoplay: false,
  autoplaySpeed: 2000,
  speed: 500,
  slidesToShow: 3,
  slidesToScroll: 1,
  variableWidth: false,
  focusOnSelect: false,
  centerMode: false,
  responsive: [
    { breakpoint: 1300, settings: { slidesToShow: 2 } },
    { breakpoint: 991, settings: { slidesToShow: 2 } },
    { breakpoint: 767, settings: { slidesToShow: 1, centerMode: true } },
    { breakpoint: 480, settings: { slidesToShow: 1, centerMode: true } },
  ],
};

EnhancedServices.defaultProps = {
  sliderSettings,
};

export default EnhancedServices;
